import { useEffect, useState } from 'react';
import { listTasks } from '../utils/apiClient';

/**
 * Per-group open-task counters for the sidebar badge. Single module-level
 * cache + listener set, fetched once on mount; consumers can `refresh()`
 * after mutations. Counts only open (non-completed, non-deleted) tasks
 * grouped by `teamId`.
 */

interface Counts {
  [teamId: string]: number;
}

const listeners = new Set<() => void>();
let cache: Counts | null = null;
let inflight: Promise<void> | null = null;

export async function refreshTaskCounts(): Promise<void> {
  if (inflight) return inflight;
  inflight = (async () => {
    try {
      const tasks = await listTasks('combined');
      const next: Counts = {};
      for (const t of tasks) {
        if (t.status === 'completed' || t.deletedAt) continue;
        next[t.teamId] = (next[t.teamId] ?? 0) + 1;
      }
      cache = next;
      for (const l of listeners) l();
    } finally {
      inflight = null;
    }
  })();
  return inflight;
}

export function useTaskCounts(): { counts: Counts; refresh: () => Promise<void> } {
  const [, force] = useState(0);
  useEffect(() => {
    const fn = (): void => force((n) => n + 1);
    listeners.add(fn);
    return () => {
      listeners.delete(fn);
    };
  }, []);
  useEffect(() => {
    if (!cache && !inflight) {
      void refreshTaskCounts().catch(() => {
        // Leave counts empty; sidebar just omits badges.
      });
    }
  }, []);
  return { counts: cache ?? {}, refresh: refreshTaskCounts };
}
